import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/app/StatusBadge";
import { ERASE } from "@/constants/testIds";
import { ShieldCheck, Download } from "lucide-react";

const METHODS = ["clear", "purge", "crypto_erase", "physical_destruction"];

export default function DataErasure() {
  const [assets, setAssets] = useState([]);
  const [rows, setRows] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [method, setMethod] = useState("purge");
  const [certOpen, setCertOpen] = useState(false);
  const [cert, setCert] = useState(null);

  const load = async () => {
    const [a, e] = await Promise.all([
      api.get("/assets/", { params: { lifecycle_state: "in_warehouse", limit: 100 } }),
      api.get("/erasures", { params: { limit: 100 } }),
    ]);
    setAssets(a.data.items || []);
    setRows(e.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    if (!assetId) return toast.error("Select asset");
    try {
      await api.post("/erasures/start", { asset_id: assetId, method });
      toast.success("Erasure started");
      setAssetId("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const verify = async (id) => {
    try {
      await api.post(`/erasures/${id}/verify`, { result: "success", verification: "verified" });
      toast.success("Erasure certified");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const download = async (id) => {
    const { data } = await api.get(`/erasures/${id}/certificate`);
    setCert(data);
    setCertOpen(true);
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Operations</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Data Erasure</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Designed to support NIST SP 800-88 Rev.2 sanitisation workflows.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Asset</Label>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid={ERASE.assetSelect} className="h-10 mt-1"><SelectValue placeholder="Select asset…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {assets.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid={ERASE.startBtn} onClick={start} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Start erasure
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500">Certificate</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Asset</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Method</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Result</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Started</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No erasures yet.</TableCell></TableRow>
            ) : rows.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs">{e.certificate_number}</TableCell>
                <TableCell className="font-mono text-xs">{e.asset_id?.slice(0, 8)}</TableCell>
                <TableCell className="text-sm capitalize">{e.method?.replace(/_/g, " ")}</TableCell>
                <TableCell><StatusBadge value={e.result || "in_progress"} /></TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(e.start_time || e.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <div className="flex gap-1.5">
                    {!e.result && (
                      <Button data-testid={ERASE.verifyBtn} size="sm" variant="outline" onClick={() => verify(e.id)}>
                        Verify
                      </Button>
                    )}
                    {e.result === "success" && (
                      <Button data-testid={ERASE.downloadCert} size="sm" variant="outline" onClick={() => download(e.id)}>
                        <Download className="h-4 w-4 mr-1" /> Certificate
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sanitisation Certificate</DialogTitle></DialogHeader>
          {cert && (
            <div className="text-sm text-slate-800 space-y-3">
              <div className="text-center border border-slate-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Certificate No.</div>
                <div className="font-mono text-lg mt-1">{cert.certificate_number}</div>
                <div className="text-xs text-slate-500 mt-2">{new Date(cert.issued_at).toLocaleString()}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">Serial:</span> <span className="font-mono">{cert.asset?.serial}</span></div>
                <div><span className="text-slate-500">Tag:</span> <span className="font-mono">{cert.asset?.asset_tag}</span></div>
                <div><span className="text-slate-500">Model:</span> {cert.asset?.brand} {cert.asset?.model}</div>
                <div><span className="text-slate-500">Method:</span> <span className="capitalize">{cert.method?.replace(/_/g, " ")}</span></div>
                <div><span className="text-slate-500">Tool:</span> {cert.tool} v{cert.tool_version}</div>
                <div><span className="text-slate-500">Result:</span> <span className="capitalize">{cert.result}</span></div>
                <div><span className="text-slate-500">Operator:</span> {cert.operator}</div>
                <div><span className="text-slate-500">Reviewer:</span> {cert.reviewer}</div>
              </div>
              <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-3">
                {cert.sanitisation_standard}. {cert.disclaimer}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => window.print()}><Download className="h-4 w-4 mr-1.5" />Print / Save PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
